import './popupUpsert.scss';

import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Form, Button, Popup, ScrollView } from "devextreme-react";
import { Position, ToolbarItem } from "devextreme-react/popup";
import { CustomRule, FormRef, GroupItem, Item } from "devextreme-react/form";
import { useScreenSize } from "../../../../utils/media-query";
import { ClickEvent } from "devextreme/ui/button";

import useApiConfiguration from '../../../../apiConfig';
import {
    CommesseApi,
    PersonaleApi, PersonaleRapportinoDTO,
    PersonaleRapportinoDTOFromJSON, PersonaleRapportinoDTOToJSON
} from "../../../../data";

export type PopupPersonaleEditHandle = {
    show: (json: string, idx: number, idCliente: number, isUpdate: boolean) => void
};

type Props = {
    onChange: (json: string, idx: number) => void
};

type State = {
    json: string,
    idx: number,
    idCliente: number,
    visible: boolean,
    isUpdate: boolean
};

const PopupPersonaleEdit: React.ForwardRefRenderFunction<PopupPersonaleEditHandle, Props> = (props, ref) => {
    const emptyData: PersonaleRapportinoDTO = {
        idPersonale: undefined,
        dataAMattino: null,
        dataAPomeriggio: null,
        dataDaMattino: null,
        dataDaPomeriggio: null,
        dataIntervento: undefined,
        oreTotali: 0,
        oreViaggio: 0,
        pernottamenti: 0,
        personale: null,
        viaggio: 0,
        idCommessa: undefined,
        commessa: null,
        descrizioneCommessa: null
    };

    const apiConfig = useApiConfiguration();
    const formRef = useRef<FormRef>(null);
    const { isLarge, isMedium, isSmall, isXSmall } = useScreenSize();

    const [state, setState] = useState<State>({ json: '', idx: undefined, idCliente: undefined, visible: false, isUpdate: false });
    const [formData, setFormData] = useState<PersonaleRapportinoDTO>();
    const [dataOdierna, setDataOdierna] = useState(new Date());
    const [personaleId, setPersonaleId] = useState<number | null>(null);
    const [personaleDataSource, setPersonaleDataSource] = useState<any>(null);
    const [commessaDataSource, setCommessaDataSource] = useState<any>(null);

    function hideInfo() {
        setState({ json: '', idx: undefined, idCliente: undefined, visible: false, isUpdate: false });
    }

    useImperativeHandle(ref, () => ({
        show(json, idx, idCliente, isUpdate) {
            setState({ json: json, idx: idx, idCliente: idCliente, visible: true, isUpdate: isUpdate });
        }
    }));

    useEffect(() => {
        if (state.visible) {
            if (state.json != '') {
                var obj = JSON.parse(state.json);
                var data = PersonaleRapportinoDTOFromJSON(obj);

                if (!state.isUpdate) {
                    data.idPersonale = undefined;
                    data.personale = null;
                    data.idCommessa = undefined;
                    data.commessa = null;
                    data.dataIntervento = undefined;
                }

                setFormData({ ...data });
            } else {
                setFormData({ ...emptyData });

                const api = new PersonaleApi(apiConfig);
                api.getPersonaleByIdCliente({
                    idCliente: state.idCliente
                }).then((res) => setPersonaleDataSource(res));
            }
        }
    }, [state.json, state.idx, state.idCliente, state.visible, state.isUpdate]);

    function setDateToTime(date: Date, time: Date) {
        if (!time) return null;

        const newDate = new Date(date);
        newDate.setHours(time.getHours(), time.getMinutes(), 0, 0);
        return newDate;
    };

    function setTimeToDate(date: Date, ore: number) {
        if (!date) return null;

        const newDate = new Date(date);
        newDate.setHours(ore, 0, 0, 0);
        return newDate;
    };

    const validateDaMattino = (e) => {
        const dataDa = e.value;

        if ((dataDa && !formData.dataAMattino) || (!dataDa && formData.dataAMattino))
            return false;

        if (dataDa && formData.dataAMattino)
            return setDateToTime(dataOdierna, dataDa) < setDateToTime(dataOdierna, formData.dataAMattino);

        return true;
    };

    const validateAMattino = (e) => {
        const dataA = e.value;

        if ((formData.dataDaMattino && !dataA) || (!formData.dataDaMattino && dataA))
            return false;

        if (formData.dataDaMattino && dataA)
            return setDateToTime(dataOdierna, formData.dataDaMattino) < setDateToTime(dataOdierna, dataA);

        return true;
    };

    const validateDaPomeriggio = (e) => {
        const dataDa = e.value;

        if ((dataDa && !formData.dataAPomeriggio) || (!dataDa && formData.dataAPomeriggio))
            return false;

        if (dataDa && formData.dataAPomeriggio)
            return setDateToTime(dataOdierna, dataDa) < setDateToTime(dataOdierna, formData.dataAPomeriggio);

        return true;
    };

    const validateAPomeriggio = (e) => {
        const dataA = e.value;

        if ((formData.dataDaPomeriggio && !dataA) || (!formData.dataDaPomeriggio && dataA))
            return false;

        if (formData.dataDaPomeriggio && dataA)
            return setDateToTime(dataOdierna, formData.dataDaPomeriggio) < setDateToTime(dataOdierna, dataA);

        return true;
    };

    const onFieldDataChanged = (e) => {
        if (!state.isUpdate) {
            if (e.dataField == "dataDaMattino" || e.dataField == "dataAMattino" || e.dataField == "dataDaPomeriggio" || e.dataField == "dataAPomeriggio")
                formRef.current?.instance().validate();

            if (e.dataField === "idPersonale" && e.value !== personaleId) {
                formData.idCommessa = null;
                setPersonaleId(e.value || null);
            }
        }
    }

    const upsert = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            //setto un'ora standard al giorno dell'intervento (così posso confrontarla in altri contesti)
            var dataIntervento = setTimeToDate(formData.dataIntervento, 4);

            //calcolo ore totali
            let oreTotali = 0;
            var dataDaMattino = setDateToTime(dataIntervento, formData.dataDaMattino);
            var dataAMattino = setDateToTime(dataIntervento, formData.dataAMattino);
            var dataDaPomeriggio = setDateToTime(dataIntervento, formData.dataDaPomeriggio);
            var dataAPomeriggio = setDateToTime(dataIntervento, formData.dataAPomeriggio);

            if (dataDaMattino && dataAMattino) {
                const diffMs = dataAMattino.getTime() - dataDaMattino.getTime()
                if (diffMs > 0) oreTotali += diffMs / (1000 * 60 * 60);
            }

            if (dataDaPomeriggio && dataAPomeriggio) {
                const diffMs = dataAPomeriggio.getTime() - dataDaPomeriggio.getTime();
                if (diffMs > 0) oreTotali += diffMs / (1000 * 60 * 60);
            }

            formData.oreTotali = (Math.round(oreTotali * 10) / 10);
            formData.dataIntervento = dataIntervento;
            formData.dataDaMattino = dataDaMattino;
            formData.dataAMattino = dataAMattino;
            formData.dataDaPomeriggio = dataDaPomeriggio;
            formData.dataAPomeriggio = dataAPomeriggio;

            var json = JSON.stringify(PersonaleRapportinoDTOToJSON(formData));
            props.onChange(json, state.idx);
            hideInfo();
        }
    };

    useEffect(() => {
        if (personaleId) {
            const api = new CommesseApi(apiConfig);
            api.getCommesse({
                data: null,
                idCliente: state.idCliente,
                idPersonale: personaleId
            }).then((res) => setCommessaDataSource(res));
        } else
            setCommessaDataSource([]);
    }, [personaleId]);

    return (
        <React.Fragment>
            <Popup
                visible={state.visible}
                dragEnabled hideOnOutsideClick
                showCloseButton showTitle
                fullScreen={(isSmall || isXSmall ? true : false)}
                title="Personale"
                container=".dx-viewport"
                onHidden={hideInfo}
            >
                <Position at="center" my="center" collision="fit" />

                <ToolbarItem widget='dxButton' toolbar='bottom' location='center'>
                    <Button
                        onClick={upsert}
                        icon="check"
                        text='Salva'
                        type='default'
                        stylingMode='contained'
                    />
                </ToolbarItem>

                <ScrollView>
                    <Form
                        className='rapportino-personale-edit'
                        ref={formRef} formData={formData}
                        labelMode='floating' labelLocation='left'
                        colCount={(isLarge || isMedium) ? 2 : 1}
                        onFieldDataChanged={onFieldDataChanged}
                    >
                        <Item colSpan={1} dataField='dataIntervento' label={{ text: "Data" }}
                            isRequired={true} editorType='dxDateBox'
                            editorOptions={{
                                type: "date",
                                disabled: state.isUpdate,
                                displayFormat: (isLarge || isMedium) ? "EEEE d MMMM yyyy" : "dd/MM/yyyy",
                                openOnFieldClick: !state.isUpdate
                            }}
                        />

                        <Item itemType='empty' colSpan={1} visible={(isLarge || isMedium)} />

                        {
                            state.isUpdate ?
                                (
                                    <Item colSpan={1} dataField="personale" editorType="dxTextBox" editorOptions={{ disabled: true }} />
                                ) :
                                (
                                    <Item colSpan={1} dataField="idPersonale" label={{ text: "Personale" }}
                                        editorType="dxSelectBox" isRequired={true}
                                        editorOptions={{
                                            disabled: false,
                                            dataSource: personaleDataSource,
                                            displayExpr: 'descrizione',
                                            valueExpr: 'idPersonale',
                                            searchEnabled: true,
                                            showClearButton: true,
                                            onValueChanged: (e) => {
                                                if (!formData) return;
                                                formData.personale = e.value != null ? e.component.option('text') : "";
                                            }
                                        }}
                                    />
                                )
                        }

                        {
                            state.isUpdate ?
                                (
                                    <Item colSpan={1} dataField='commessa' editorType='dxTextBox' editorOptions={{ disabled: true }} />
                                ) :
                                (
                                    <Item colSpan={1} dataField='idCommessa' label={{ text: "Commessa" }}
                                        editorType='dxLookup' isRequired={true}
                                        editorOptions={{
                                            disabled: !personaleId,
                                            dataSource: commessaDataSource,
                                            displayExpr: 'descrizione',
                                            valueExpr: 'idCommessa',
                                            searchEnabled: true,
                                            showClearButton: true,
                                            onValueChanged: (e) => {
                                                if (!formData) return;
                                                formData.commessa = e.value != null ? e.component.option('text') : "";
                                            }
                                        }}
                                    />
                                )
                        }

                        <Item colSpan={2} dataField='descrizioneCommessa' label={{ text: "Descrizione Lavoro" }}
                            editorType='dxTextArea' cssClass='dx-textarea-custom' isRequired={true}
                            editorOptions={{
                                maxLenght: 1500,
                                autoResizeEnabled: true
                            }}
                        />

                        <GroupItem caption="Ore Mattino / Giorno" colSpan={1} colCount={2}>
                            <Item colSpan={1} dataField="dataDaMattino" label={{ text: "Dalle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateDaMattino}
                                    message="Orario non consentito"
                                />
                            </Item>

                            <Item colSpan={1} dataField="dataAMattino" label={{ text: "Alle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateAMattino}
                                    message="Orario non consentito"
                                />
                            </Item>
                        </GroupItem>

                        <GroupItem caption="Ore Pomeriggio" colSpan={1} colCount={2}>
                            <Item colSpan={1} dataField="dataDaPomeriggio" label={{ text: "Dalle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateDaPomeriggio}
                                    message="Orario non consentito"
                                />
                            </Item>

                            <Item colSpan={1} dataField="dataAPomeriggio" label={{ text: "Alle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateAPomeriggio}
                                    message="Orario non consentito"
                                />
                            </Item>
                        </GroupItem>

                        <Item colSpan={1} isRequired={true} dataField="oreViaggio" editorType="dxNumberBox"
                            editorOptions={{
                                default: 0,
                                min: 0
                            }}
                        />
                    </Form>
                </ScrollView>
            </Popup>
        </React.Fragment>
    )
};

export default React.forwardRef(PopupPersonaleEdit);